import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { FiltersBar, FilterTextField, searchToFilters } from '../../../Utils/Filters/Filter'
import { useTranslation } from 'react-i18next'
import { FilterFromDate, FilterToDate } from '../../../Utils/Filters'
import moment from 'moment'
import { useThemeContext } from '../../../../theme/ThemeContext'
import { getToken } from '../../../../utils/fetchApi'
import { Stack, darken, MenuItem, Select, Paper, TableHead, Table, TableRow, TableBody, TableCell, CircularProgress, Button, LinearProgress, Typography, Box, Grid, TableContainer } from '@mui/material';
import { ISTableTitle } from '../../../Utils/ISTable'
import { formattedCurrency } from '../../../../utils/StringUtils'
import { FiletypeXls } from 'react-bootstrap-icons'
import { GoalStatsType } from '../../../../reducers/goal/goalStats'
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts'
import { isVendor, SessionState } from '../../../../reducers/auth/session'
import { connect } from 'react-redux'
import { AppState } from '../../../../reducers'

type ColumnKey = 'TOTAL' | 'FUSIOGAS' | 'HIDRO 3' | 'HIDROFLEX' | 'RESTO'

interface GoalRow {
  codeZone?: string
  codeVendor?: string
  zone?: string
  vendor?: string
  total?: number,
  anual?: number
  annualGoals?: number
  goals?: Record<string, number>
  orders?: Record<string, number>
  scope?: Record<string, number>
  deficit?: Record<string, number>
  projection?: Record<string, number>
  goal2UnlockPercentage?: number
  clients?: Record<string, { mes: number, anual: number }>
  count?: number
  totalGoals?: number
}

type Totals = {
  total: number
  anual: number
  annualGoals: number
  goals: Record<string, number>
  orders: Record<string, number>
  scope: Record<string, number>
  deficit: Record<string, number>
  projection: Record<string, number>
  clients?: Record<string, { mes: number, anual: number }>
}

type Aggregated = {
  name: string
  codeVendor?: string
  goals: number
  orders: number
  deficit: number
  clientMes: number
  clientAnual: number
}

interface Props {
  session: SessionState
}


const GoalStatsVendorSSE: React.FC<Props> = ({ session }) => {
  const history = useHistory()
  const { t } = useTranslation(['goal', 'person', 'client'])
  const { theme } = useThemeContext()
  const [selectedKey, setSelectedKey] = useState<ColumnKey>('TOTAL')
  const [groupBy, setGroupBy] = useState<"zone" | "vendor">("vendor")
  const [goalFilters, setGoalFilters] = useState(searchToFilters(history.location.search))
  const [progressList, setProgressList] = useState<GoalRow>({})
  const [goalStatsPartial, setGoalStatsPartial] = useState<GoalRow[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const eventSourceRef = useRef<EventSource | null>(null)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [orderBy,] = useState<keyof GoalStatsType | 'scope' | 'deficit' | null>(null)
  const [orderDirection,] = useState<'asc' | 'desc'>('asc')

  const rowKey = (r: GoalRow) => `${r.codeZone ?? ''}::${r.codeVendor ?? ''}`
  const [highlightedKeys, setHighlightedKeys] = useState<Set<string>>(new Set())


  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map())
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const getWorkingDays = (start: Date, end: Date) => {
    let count = 0;
    const current = new Date(start);
    while (current <= end) {
      const day = current.getDay();
      if (day !== 0 && day !== 6) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  const toDate = moment().tz("America/Argentina/Buenos_Aires").toDate()
  const firstDay = new Date(toDate.getFullYear(), toDate.getMonth(), 1);
  const lastDay = new Date(toDate.getFullYear(), toDate.getMonth() + 1, 0);

  const { diasHabilesMes, diasFaltantes } = useMemo(() => {
    const filters = searchToFilters(history.location.search);
    return {
      diasHabilesMes: Number(filters.workingDaysMonth ?? 0),
      diasFaltantes: Number(filters.remainingDays ?? 0)
    };
  }, [history.location.search]);



  const initDefaultFilters = {
    from: moment().startOf('month').tz("America/Argentina/Buenos_Aires").format(),
    to: moment().endOf('month').tz("America/Argentina/Buenos_Aires").format(),
    workingDaysMonth: getWorkingDays(firstDay, lastDay).toString(),
    remainingDays: getWorkingDays(toDate, lastDay).toString(),
    personType: 'V'
  }

  const sortedGoals = [...goalStatsPartial].sort((a, b) => {
    if (!orderBy) return 0

    let aVal: number | string = 0
    let bVal: number | string = 0

    switch (orderBy) {
      case 'codeZone':
        return orderDirection === 'asc'
          ? (Number(a.codeZone ?? 0) - Number(b.codeZone ?? 0)) ||
          (Number(a.codeVendor ?? 0) - Number(b.codeVendor ?? 0))
          : (Number(b.codeZone ?? 0) - Number(a.codeZone ?? 0)) ||
          (Number(b.codeVendor ?? 0) - Number(a.codeVendor ?? 0))
      default:
        const rawA = a[orderBy]
        const rawB = b[orderBy]

        aVal = typeof rawA === 'object' && rawA !== null
          ? JSON.stringify(rawA)
          : (rawA as string | number | undefined) ?? 0

        bVal = typeof rawB === 'object' && rawB !== null
          ? JSON.stringify(rawB)
          : (rawB as string | number | undefined) ?? 0
        break
    }


    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return orderDirection === 'asc' ? aVal - bVal : bVal - aVal
    }

    return orderDirection === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal))
  })

  const highlightRows = (group: string, color: 'red' | 'green') => {
    const newSet = new Set<string>()

    sortedGoals.forEach((row) => {
      const belongs = (groupBy === 'zone' ? row.zone : row.vendor) === group
      if (!belongs) return

      const value = row.scope?.[selectedKey] ?? 0
      const ok = groupBy === 'vendor' ? true : color === 'red' ? value < 1 : value >= 1
      if (ok) newSet.add(rowKey(row))
    })

    setHighlightedKeys(newSet)
  }

  const handleDownload = async () => {
    if (!downloadUrl) return
    try {
      const token = getToken()

      const res = await fetch(`${process.env.REACT_APP_API_URL}${downloadUrl}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      const blob = await res.blob()
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)

      const from = searchToFilters(history.location.search).from
      link.download = `objetivos_vendedores_${moment(from).tz("America/Argentina/Buenos_Aires").format('YYYY-MM')}_industrias_saladillo.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Error descargando Excel", err)
    }
  }


  const startGoalStatsStream = () => {
    setProgressList({})
    setGoalStatsPartial([])
    setIsFetching(true)
    setDownloadUrl(null)

    const params = new URLSearchParams(history.location.search).toString()
    const url = `${process.env.REACT_APP_API_URL}/goals-stats-vendors-sse?${params}&access_token=${getToken()}`

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const es = new EventSource(url)
    eventSourceRef.current = es

    es.addEventListener("progress", (event) => {
      try {
        const parsed = JSON.parse(event.data)
        const progress: GoalRow = {
          codeZone: parsed.codeZone,
          codeVendor: parsed.codeVendor,
          zone: parsed.zone,
          vendor: parsed.vendor,
          goals: parsed.goals,
          total: parsed.total,
          count: parsed.count,
          totalGoals: parsed.totalGoals,
          orders: parsed.orders,
          anual: parsed.anual,
          annualGoals: parsed.annualGoals,
          scope: parsed.scope,
          deficit: parsed.deficit,
          projection: parsed.projection,
          goal2UnlockPercentage: parsed.goal2UnlockPercentage,
          clients: parsed.clients
        }

        setProgressList(progress)
        setGoalStatsPartial((prev) => [...prev, progress])

      } catch (err) {
        console.error("Error parseando progress SSE", err)
      }
    })

    es.addEventListener("done", async (event) => {
      try {
        const data = JSON.parse(event.data)
        setProgressList(data)

        const { downloadUrl } = data
        setDownloadUrl(downloadUrl)
        setIsFetching(false)

        es.close()
        eventSourceRef.current = null
      } catch (err) {
        console.error("Error en SSE done", err)
      }
    })

    es.addEventListener("error", (err) => {
      console.error("SSE error", err)
      es.close()
      eventSourceRef.current = null
      setIsFetching(false)
    })
  }

  const stopProcess = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
      setIsFetching(false)
      setProgressList({})
      setGoalStatsPartial([])
    }
  }

  const tableBodyRef = useRef<HTMLTableSectionElement | null>(null)


  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  useEffect(() => {
    const onSearchChange = () => {
      setGoalFilters(searchToFilters(history.location.search))
    }
    onSearchChange()
  }, [history.location.search])

  useEffect(() => {
    if (!highlightedKeys || highlightedKeys.size === 0) return;

    const firstKey = highlightedKeys.values().next().value;

    const el = rowRefs.current.get(firstKey ?? '');

    const container = tableContainerRef.current;

    if (!el) {
      console.warn("No se encontró el row para esa key");
      return;
    }
    if (container) {
      const elRect = el.getBoundingClientRect();
      const contRect = container.getBoundingClientRect();

      const offsetTop = elRect.top - contRect.top + container.scrollTop;

      const target = Math.max(0, offsetTop - (container.clientHeight / 4));

      container.scrollTo({ top: target, behavior: 'smooth' });
    } else {
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        console.warn("⚠️ No encontré el rowElement en rowRefs para la key:", firstKey);
      }
    }
  }, [highlightedKeys]);


  const aggregateBy = (key: "zone" | "vendor") => {
    const map = new Map<string, Aggregated>()

    for (const row of goalStatsPartial) {
      const group = row[key]
      if (!group) continue

      const orders = Number(row.orders ? row.orders[selectedKey] : 0)
      const goals = Number(row.goals ? row.goals[selectedKey] : 0)
      const deficit = Number(row.deficit ? row.deficit[selectedKey] : 0)
      const clientMes = Number(row.clients ? row.clients[selectedKey].mes : 0)
      const clientAnual = Number(row.clients ? row.clients[selectedKey].anual : 0)

      if (!map.has(group)) {
        map.set(group, {
          name: group,
          codeVendor: row.codeVendor,
          orders, goals, deficit, clientMes, clientAnual
        })
      } else {
        const agg = map.get(group)!
        agg.orders += orders
        agg.goals += goals
        agg.deficit += deficit
        agg.clientMes += clientMes
        agg.clientAnual += clientAnual
      }
    }

    return Array.from(map.values())
  }

  const chartData = aggregateBy(groupBy).sort((a, b) => {
    const aCode = parseInt(a.codeVendor ?? "0", 10);
    const bCode = parseInt(b.codeVendor ?? "0", 10);
    return aCode - bCode;
  }).map(row => {
    return {
      ...row,
      scopePercent: row.goals ? ((row.orders / row.goals) * 100).toFixed(0) : "0"
    }
  });

  const yAxisFormatter = (value: number) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)} M`
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)} K`
    return value.toString()
  }

  const totalsByZone: Record<string, Totals> = {};

  sortedGoals.forEach((r) => {
    const zone = r.zone!;
    if (!totalsByZone[zone]) {
      totalsByZone[zone] = {
        total: 0,
        anual: 0,
        annualGoals: 0,
        goals: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
        orders: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
        scope: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
        deficit: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
        projection: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
        clients: {
          TOTAL: { mes: 0, anual: 0 },
          FUSIOGAS: { mes: 0, anual: 0 },
          "HIDRO 3": { mes: 0, anual: 0 },
          HIDROFLEX: { mes: 0, anual: 0 },
          RESTO: { mes: 0, anual: 0 }
        }
      };
    }

    const acc = totalsByZone[zone];
    acc.total += r.total ?? 0;
    acc.anual += r.anual ?? 0;
    acc.annualGoals += r.annualGoals ?? 0;

    ['TOTAL', 'FUSIOGAS', 'HIDRO 3', 'HIDROFLEX', 'RESTO'].forEach((k) => {
      acc.goals[k] = (acc.goals[k] ?? 0) + (r.goals?.[k] ?? 0);
      acc.orders[k] = (acc.orders[k] ?? 0) + (r.orders?.[k] ?? 0);
      acc.deficit[k] = (acc.deficit[k] ?? 0) + (r.deficit?.[k] ?? 0);

      if (acc.clients && r.clients && acc.clients[k] && r.clients[k]) {
        acc.clients[k].mes += r.clients[k].mes ?? 0;
        acc.clients[k].anual += r.clients[k].anual ?? 0;
      }
    });
  });

  Object.values(totalsByZone).forEach((acc) => {
    ['TOTAL', 'FUSIOGAS', 'HIDRO 3', 'HIDROFLEX', 'RESTO'].forEach((k) => {
      const pedidos = acc.orders[k];
      const objetivo = acc.goals[k];
      acc.scope[k] = objetivo ? (pedidos / objetivo) * 100 : 0;
      acc.projection[k] = objetivo
        ? (((objetivo / diasHabilesMes) * diasFaltantes) + pedidos) / objetivo * 100
        : 0;
    });
  });

  const zoneStyle = (value?: number) => {
    return {
      fontWeight: 'bold',
      backgroundColor: value && value >= 100 ? '#008457' : value ? '#d32f2f' : value === 0 ? '#d32f2f' : '#8bae74',
      color: value && value >= 100 ? 'white' : value ? 'white' : value === 0 ? 'white' : '#363636' //color: value ? 'white' : '#363636'
    }
  }

  const vendorStyle = (value?: number) => {
    return {
      fontWeight: 'bold',
      backgroundColor: value && value >= 1 ? '#008457' : value ? '#d32f2f' : value === 0 ? '#d32f2f' : '#d32f2f', // 'inherit',
      color: 'white'
    }
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const data = payload[0].payload;

    return (
      <Paper sx={{ borderColor: theme.palette.success.main, borderStyle: "solid", borderWidth: 1, p: 1.5, maxWidth: 350 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ marginBottom: "15px" }}>
          {data.name}
        </Typography>

        <Grid container spacing={0.5} direction="column">
          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("budget")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {formattedCurrency(data.goals)}
            </Typography>
          </Grid>

          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("orders")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {formattedCurrency(data.orders)}
            </Typography>
          </Grid>

          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("deficit")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {formattedCurrency(Number(data.deficit ?? 0))}
            </Typography>
          </Grid>

          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("scope")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {data.goals
                ? ((data.orders / data.goals) * 100).toFixed(2) + "%"
                : "0%"}
            </Typography>
          </Grid>

          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("projection")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {data.goals
                ? (
                  ((((data.goals / diasHabilesMes) * diasFaltantes) + data.orders) /
                    data.goals) *
                  100
                ).toFixed(2) + "%"
                : "0%"}
            </Typography>
          </Grid>

          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("clients")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {data.clientMes} / {data.clientAnual}
            </Typography>
          </Grid>

          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("scopeClients")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {data.clientMes && data.clientAnual
                ? ((data.clientMes / data.clientAnual) * 100).toFixed(2) + "%"
                : "0%"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>);
  };



  return (
    <Stack style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100% - var(--header-height)' }} >
      <Stack display="flex" flexDirection='row' justifyContent="normal" alignItems="center" flexWrap="wrap" spacing={1}>
        <FiltersBar defaultFilters={initDefaultFilters}>
          <FilterFromDate name="from" label={t("from")} />
          <FilterToDate name="to" label={t("to")} />
          <FilterTextField name="workingDaysMonth" label={t("workingDaysMonth")} />
          <FilterTextField name="remainingDays" label={t("remainingDays")} />
        </FiltersBar>
        <Stack display="flex" flexDirection='row' alignItems="baseline" flexWrap="wrap" spacing={1}>
          <Button
            id="download"
            aria-label="download"
            size='large'
            style={{ marginRight: '20px', textTransform: 'none' }}
            onClick={startGoalStatsStream}
            disabled={isFetching || !goalFilters.personType}>
            {t('generate')}
          </Button>

          <Button
            style={{ marginRight: '20px', textTransform: 'none' }}
            onClick={stopProcess}
            size='large'
            disabled={!isFetching}
          >
            {t('cancel')}
          </Button>

          <Button
            variant="contained"
            onClick={handleDownload}
            size='large'
            style={{ marginRight: '20px', textTransform: 'none' }}
            disabled={!downloadUrl}
          >
            {t('downloadReport')}&nbsp;&nbsp;<FiletypeXls size={25} />
          </Button>
        </Stack>
      </Stack>


      <Paper style={{ borderRadius: "10px", margin: "10px", padding: "10px" }}>
        <Box>
          <Grid container spacing={2} alignItems="center">
            {!isVendor(session) && (
              <Grid item xs={4}>
                <Typography component='span'>{t('zone')}:&nbsp;&nbsp;</Typography>
                <Typography component='span' sx={{ fontWeight: 'bold' }}>{progressList.codeZone ? `${progressList.codeZone} - ${progressList.zone}` : ''}</Typography>
              </Grid>
            )}
            <Grid item xs={4}>
              <Typography component='span'>{t('vendor')}:&nbsp;&nbsp;</Typography>
              <Typography component='span' sx={{ fontWeight: 'bold' }}>{progressList.codeVendor ? `${progressList.codeVendor} - ${progressList.vendor}` : ''}</Typography>
            </Grid>

            {progressList.count != null && progressList.totalGoals != null && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box flexGrow={1}>
                    <LinearProgress
                      color='success'
                      variant="determinate"
                      value={(progressList.count / progressList.totalGoals) * 100}
                      style={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ minWidth: 50 }}>
                    {progressList.count}/{progressList.totalGoals}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {isFetching &&
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
            <CircularProgress size={25} />
          </div>
        }
      </Paper>
      <Paper style={{ borderRadius: "10px", margin: "10px", padding: "10px" }}>
        <Box display="flex" justifyContent="flex-start" alignItems={"center"} mb={2}>
          <Typography variant="body1">{t("graph")}</Typography>
          {!isVendor(session) && <Select
            value={groupBy}
            onChange={() => setGroupBy(groupBy === "zone" ? "vendor" : "zone")}
            sx={{ ml: 1, mr: 1 }}
            size='small'
          >
            {(['zone', 'vendor']).map((k) => (
              <MenuItem key={k} value={k}>
                {t(k)}
              </MenuItem>
            ))}
          </Select>}
          <Typography variant="body1" sx={{ ml: 1, mr: 1 }}>{t('grouppedBy')}</Typography>

          <Select
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value as ColumnKey)}
            sx={{ ml: 1, mr: 1 }}
            size='small'
          >
            {(['TOTAL', 'FUSIOGAS', 'HIDRO 3', 'HIDROFLEX', 'RESTO'] as ColumnKey[]).map((k) => (
              <MenuItem key={k} value={k}>
                {k}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <ResponsiveContainer width="100%" height={400}>
          {chartData.length > 0 ? (
            <BarChart data={chartData}>
              <XAxis
                dataKey="name"
                angle={-30}
                textAnchor="end"
                interval={0}
                height={60}
                tick={({ x, y, payload }) => {
                  if (groupBy === 'vendor') return <></>
                  const words = payload.value.split(" ");
                  return (
                    <g transform={`translate(${x},${y + 20})`}>
                      {words.map((word, index) => (
                        <text
                          key={index}
                          fill={theme.palette.text.secondary}
                          x={0}
                          y={index * 14}
                          textAnchor="middle"
                          fontSize={12}
                        >
                          {word}
                        </text>
                      ))}
                    </g>
                  );
                }} />
              <YAxis tickFormatter={yAxisFormatter} />
              <Tooltip cursor={{ fill: theme.palette.action.hover }} content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                align="right"
                layout="horizontal"
                wrapperStyle={{ top: 0, right: 0 }}
                formatter={(value) => {
                  return (t(value))
                }}
              />
              <Bar dataKey="orders" stackId="a" fill="#008457"
                onClick={(data, index) => highlightRows(data.name ?? '', 'green')}
              >
                <LabelList
                  dataKey="scopePercent"
                  content={(props: any) => {
                    const { x, y, width, value } = props;
                    return (
                      <text
                        x={x + width / 2}
                        y={y + 15}
                        fill="#fff"
                        textAnchor="middle"
                        fontSize={12}
                      >
                        {value}%
                      </text>
                    );
                  }}
                />
              </Bar>
              <Bar dataKey="deficit" stackId="a" fill="#F44336"
                onClick={(data, index) => highlightRows(data.name ?? '', 'red')}
              />

            </BarChart>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body2" color="text.secondary">
                {t("Sin Datos")}
              </Typography>
            </Box>
          )}
        </ResponsiveContainer>
      </Paper>


      <Stack style={{ display: 'flex', flexDirection: 'column', height: '75%', borderRadius: "10px", margin: "10px", minWidth: "500px" }}>
        <ISTableTitle title={t('title')} />
        <TableContainer
          ref={tableContainerRef}
          sx={{ overflowY: 'auto', overflowX: 'auto', height: '100%' }}
        >

          <Table stickyHeader size="medium" >
            <TableHead>
              <TableRow selected={true}>
                {!isVendor(session) && <TableCell align="left">{t('zone')}</TableCell>}
                <TableCell align="left">{t('vendor')}</TableCell>
                <TableCell align="left"></TableCell>
                <TableCell align="right">TOTAL</TableCell>
                <TableCell align="right">FUSIOGAS</TableCell>
                <TableCell align="right">HIDRO 3</TableCell>
                <TableCell align="right">HIDROFLEX</TableCell>
                <TableCell align="right">RESTO</TableCell>
                <TableCell align="right">{t('totalAnnual')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody ref={tableBodyRef}>
              {/* TOTAL PAÍS */}

              {sortedGoals.length > 0 && (() => {
                const totalPais = sortedGoals.reduce<Totals>((acc, r) => {
                  acc.total = (acc.total ?? 0) + (r.total ?? 0);
                  acc.anual = (acc.anual ?? 0) + (r.anual ?? 0);
                  acc.annualGoals = (acc.annualGoals ?? 0) + (r.annualGoals ?? 0);

                  ['TOTAL', 'FUSIOGAS', 'HIDRO 3', 'HIDROFLEX', 'RESTO'].forEach((k: any) => {
                    acc.goals[k] = (acc.goals[k] ?? 0) + (r.goals?.[k] ?? 0);
                    acc.orders[k] = (acc.orders[k] ?? 0) + (r.orders?.[k] ?? 0);
                    acc.deficit[k] = (acc.deficit[k] ?? 0) + (r.deficit?.[k] ?? 0);

                    if (acc.clients && r.clients && acc.clients[k] && r.clients[k]) {
                      acc.clients[k].mes += r.clients[k].mes ?? 0;
                      acc.clients[k].anual += r.clients[k].anual ?? 0;
                    }

                  })

                  return acc
                }, {
                  total: 0,
                  anual: 0,
                  annualGoals: 0,
                  goals: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
                  orders: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
                  scope: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
                  deficit: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
                  projection: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
                  clients: {
                    TOTAL: { mes: 0, anual: 0 },
                    FUSIOGAS: { mes: 0, anual: 0 },
                    "HIDRO 3": { mes: 0, anual: 0 },
                    HIDROFLEX: { mes: 0, anual: 0 },
                    RESTO: { mes: 0, anual: 0 }
                  }
                })

                for (const b of ["TOTAL", "FUSIOGAS", "HIDRO 3", "HIDROFLEX", "RESTO"]) {
                  const pedidos = totalPais.orders[b];
                  const objetivo = totalPais.goals[b];

                  totalPais.scope[b] = objetivo ? (pedidos / objetivo) * 100 : 0;
                  totalPais.projection[b] = objetivo
                    ? (((objetivo / diasHabilesMes) * diasFaltantes) + pedidos) / objetivo * 100
                    : 0;
                }

                if (!isVendor(session)) {
                  return (
                    <>
                      <TableRow sx={{ backgroundColor: '#4F81BD' }}>
                        <TableCell align="left" sx={{ color: 'white' }}>{t('totalCountry')}</TableCell>
                        <TableCell align="left" sx={{ color: 'white' }}></TableCell>
                        <TableCell align="left" sx={{ color: 'white' }}>{t('budget')}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.goals['TOTAL'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.goals['FUSIOGAS'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.goals['HIDRO 3'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.goals['HIDROFLEX'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.goals['RESTO'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.annualGoals)}</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#4F81BD' }}>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left" sx={{ color: 'white' }}>{t('orders')}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.orders['TOTAL'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.orders['FUSIOGAS'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.orders['HIDRO 3'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.orders['HIDROFLEX'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.orders['RESTO'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.anual)}</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#4F81BD' }}>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left" sx={{ color: 'white' }}>{t('scope')}</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['TOTAL'] || totalPais.orders['TOTAL'] ? totalPais.scope['TOTAL'] : undefined)}>{totalPais.scope['TOTAL'].toFixed(2)}%</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['FUSIOGAS'] || totalPais.orders['FUSIOGAS'] ? totalPais.scope['FUSIOGAS'] : undefined)}>{totalPais.scope['FUSIOGAS'].toFixed(2)}%</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['HIDRO 3'] || totalPais.orders['HIDRO 3'] ? totalPais.scope['HIDRO 3'] : undefined)}>{totalPais.scope['HIDRO 3'].toFixed(2)}%</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['HIDROFLEX'] || totalPais.orders['HIDROFLEX'] ? totalPais.scope['HIDROFLEX'] : undefined)}>{totalPais.scope['HIDROFLEX'].toFixed(2)}%</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['RESTO'] || totalPais.orders['RESTO'] ? totalPais.scope['RESTO'] : undefined)}>{totalPais.scope['RESTO'].toFixed(2)}%</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['TOTAL'] || totalPais.orders['TOTAL'] ? (totalPais.anual / totalPais.annualGoals) * 100 : undefined)}>{((totalPais.anual / totalPais.annualGoals) * 100).toFixed(2)}%</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#4F81BD' }}>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left" sx={{ color: 'white' }}>{t('deficit')}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.deficit['TOTAL'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.deficit['FUSIOGAS'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.deficit['HIDRO 3'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.deficit['HIDROFLEX'])}</TableCell>
                        <TableCell align='right' sx={{ color: 'white' }}>{formattedCurrency(totalPais.deficit['RESTO'])}</TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>{formattedCurrency(Math.max(0, totalPais.annualGoals - totalPais.anual))}</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#4F81BD' }}>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left" sx={{ color: 'white' }}>{t('projection')}</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['TOTAL'] || totalPais.orders['TOTAL'] ? totalPais.projection['TOTAL'] : undefined)}>{totalPais.projection['TOTAL'].toFixed(2)}%</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['FUSIOGAS'] || totalPais.orders['FUSIOGAS'] ? totalPais.projection['FUSIOGAS'] : undefined)}>{totalPais.projection['FUSIOGAS'].toFixed(2)}%</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['HIDRO 3'] || totalPais.orders['HIDRO 3'] ? totalPais.projection['HIDRO 3'] : undefined)}>{totalPais.projection['HIDRO 3'].toFixed(2)}%</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['HIDROFLEX'] || totalPais.orders['HIDROFLEX'] ? totalPais.projection['HIDROFLEX'] : undefined)}>{totalPais.projection['HIDROFLEX'].toFixed(2)}%</TableCell>
                        <TableCell align='right' sx={zoneStyle(totalPais.goals['RESTO'] || totalPais.orders['RESTO'] ? totalPais.projection['RESTO'] : undefined)}>{totalPais.projection['RESTO'].toFixed(2)}%</TableCell>
                        <TableCell align='right'></TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#4F81BD' }}>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left" sx={{ color: 'white' }}>{t('clientsAnual')}</TableCell>
                        <TableCell align="right" >{((totalPais['clients']?.TOTAL.anual ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" >{((totalPais['clients']?.FUSIOGAS.anual ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" >{((totalPais['clients']?.['HIDRO 3'].anual ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" >{((totalPais['clients']?.HIDROFLEX.anual ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" >{((totalPais['clients']?.RESTO.anual ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" >{ }</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#4F81BD' }}>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left" sx={{ color: 'white' }}>{t('clients')}</TableCell>
                        <TableCell align="right" >{((totalPais['clients']?.TOTAL.mes ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" >{((totalPais['clients']?.FUSIOGAS.mes ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" >{((totalPais['clients']?.['HIDRO 3'].mes ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" >{((totalPais['clients']?.HIDROFLEX.mes ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" >{((totalPais['clients']?.RESTO.mes ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" >{ }</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#4F81BD' }}>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left" sx={{ color: 'white' }}>{t('scopeClients')}</TableCell>
                        <TableCell align="right">
                          {((totalPais['clients']?.TOTAL.anual
                            ? (totalPais['clients']?.TOTAL.mes / totalPais['clients']?.TOTAL.anual) * 100 : 0).toFixed(2) + '%')}
                        </TableCell>
                        <TableCell align="right">
                          {
                            ((totalPais['clients']?.FUSIOGAS.anual
                              ? (totalPais['clients']?.FUSIOGAS.mes / totalPais['clients']?.FUSIOGAS.anual) * 100 : 0).toFixed(2) + '%')
                          }
                        </TableCell>
                        <TableCell align="right">
                          {((totalPais['clients']?.['HIDRO 3'].anual
                            ? (totalPais['clients']?.['HIDRO 3'].mes / totalPais['clients']?.['HIDRO 3'].anual) * 100 : 0).toFixed(2) + '%')}
                        </TableCell>
                        <TableCell align="right">
                          {((totalPais['clients']?.HIDROFLEX.anual
                            ? (totalPais['clients']?.HIDROFLEX.mes / totalPais['clients']?.HIDROFLEX.anual) * 100 : 0).toFixed(2) + '%')}
                        </TableCell>
                        <TableCell align="right">
                          {((totalPais['clients']?.RESTO.anual
                            ? (totalPais['clients']?.RESTO.mes / totalPais['clients']?.RESTO.anual) * 100 : 0).toFixed(2) + '%')}
                        </TableCell>
                        <TableCell>{''}</TableCell>
                      </TableRow>
                    </>
                  )
                }
              })()}

              {/* Totales por zona y vendedores */}

              {(() => {
                const totalsByZone: Record<string, Totals> = {};

                sortedGoals.forEach((r) => {
                  const zone = r.zone!;
                  if (!totalsByZone[zone]) {
                    totalsByZone[zone] = {
                      total: 0,
                      anual: 0,
                      annualGoals: 0,
                      goals: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
                      orders: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
                      scope: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
                      deficit: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
                      projection: { TOTAL: 0, FUSIOGAS: 0, 'HIDRO 3': 0, HIDROFLEX: 0, RESTO: 0 },
                      clients: {
                        TOTAL: { mes: 0, anual: 0 },
                        FUSIOGAS: { mes: 0, anual: 0 },
                        "HIDRO 3": { mes: 0, anual: 0 },
                        HIDROFLEX: { mes: 0, anual: 0 },
                        RESTO: { mes: 0, anual: 0 }
                      }

                    };
                  }

                  const acc = totalsByZone[zone];
                  acc.total += r.total ?? 0;
                  acc.anual += r.anual ?? 0;
                  acc.annualGoals += r.annualGoals ?? 0;

                  ['TOTAL', 'FUSIOGAS', 'HIDRO 3', 'HIDROFLEX', 'RESTO'].forEach((k) => {
                    acc.goals[k] = (acc.goals[k] ?? 0) + (r.goals?.[k] ?? 0);
                    acc.orders[k] = (acc.orders[k] ?? 0) + (r.orders?.[k] ?? 0);
                    acc.deficit[k] = (acc.deficit[k] ?? 0) + (r.deficit?.[k] ?? 0);


                    if (acc.clients && r.clients && acc.clients[k] && r.clients[k]) {
                      acc.clients[k].mes += r.clients[k].mes ?? 0;
                      acc.clients[k].anual += r.clients[k].anual ?? 0;
                    }

                  });
                });

                Object.values(totalsByZone).forEach((acc) => {
                  ['TOTAL', 'FUSIOGAS', 'HIDRO 3', 'HIDROFLEX', 'RESTO'].forEach((k) => {
                    const pedidos = acc.orders[k];
                    const objetivo = acc.goals[k];
                    acc.scope[k] = objetivo ? (pedidos / objetivo) * 100 : 0;
                    acc.projection[k] = objetivo
                      ? (((objetivo / diasHabilesMes) * diasFaltantes) + pedidos) / objetivo * 100
                      : 0;
                  });
                });

                return Object.entries(totalsByZone).map(([zone, zoneTotals]) => (
                  <React.Fragment key={zone}>
                    {/* Total zona */}
                    {!isVendor(session) && (
                      <TableRow sx={{ backgroundColor: '#8bae74', fontWeight: 'bold' }}>
                        <TableCell align="left" sx={zoneStyle()}>{zone}</TableCell>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}>{t('budget')}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.goals['TOTAL'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.goals['FUSIOGAS'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.goals['HIDRO 3'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.goals['HIDROFLEX'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.goals['RESTO'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.annualGoals)}</TableCell>
                      </TableRow>
                    )}
                    {!isVendor(session) && (
                      <TableRow sx={{ backgroundColor: '#8bae74', fontWeight: 'bold' }}>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}>{t('orders')}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.orders['TOTAL'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.orders['FUSIOGAS'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.orders['HIDRO 3'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.orders['HIDROFLEX'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.orders['RESTO'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.anual)}</TableCell>
                      </TableRow>
                    )}
                    {!isVendor(session) && (
                      <TableRow sx={{ backgroundColor: '#8bae74', fontWeight: 'bold' }}>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}>{t('scope')}</TableCell>
                        <TableCell align="right" sx={zoneStyle(zoneTotals.goals['TOTAL'] || zoneTotals.orders['TOTAL'] ? zoneTotals.scope['TOTAL'] : undefined)}>{zoneTotals.scope['TOTAL'].toFixed(2)}%</TableCell>
                        <TableCell align="right" sx={zoneStyle(zoneTotals.goals['FUSIOGAS'] || zoneTotals.orders['FUSIOGAS'] ? zoneTotals.scope['FUSIOGAS'] : undefined)}>{zoneTotals.scope['FUSIOGAS'].toFixed(2)}%</TableCell>
                        <TableCell align="right" sx={zoneStyle(zoneTotals.goals['HIDRO 3'] || zoneTotals.orders['HIDRO 3'] ? zoneTotals.scope['HIDRO 3'] : undefined)}>{zoneTotals.scope['HIDRO 3'].toFixed(2)}%</TableCell>
                        <TableCell align="right" sx={zoneStyle(zoneTotals.goals['HIDROFLEX'] || zoneTotals.orders['HIDROFLEX'] ? zoneTotals.scope['HIDROFLEX'] : undefined)}>{zoneTotals.scope['HIDROFLEX'].toFixed(2)}%</TableCell>
                        <TableCell align="right" sx={zoneStyle(zoneTotals.goals['RESTO'] || zoneTotals.orders['RESTO'] ? zoneTotals.scope['RESTO'] : undefined)}>{zoneTotals.scope['RESTO'].toFixed(2)}%</TableCell>
                        <TableCell align='right' sx={zoneStyle((zoneTotals.anual / zoneTotals.annualGoals) * 100)}>{((zoneTotals.anual / zoneTotals.annualGoals) * 100).toFixed(2)}%</TableCell>
                      </TableRow>
                    )}
                    {!isVendor(session) && (
                      <TableRow sx={{ backgroundColor: '#8bae74', fontWeight: 'bold' }}>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}>{t('deficit')}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.deficit['TOTAL'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.deficit['FUSIOGAS'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.deficit['HIDRO 3'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.deficit['HIDROFLEX'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(zoneTotals.deficit['RESTO'])}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{formattedCurrency(Math.max(0, zoneTotals.annualGoals - zoneTotals.anual))}</TableCell>
                      </TableRow>
                    )}
                    {!isVendor(session) && (
                      <TableRow sx={{ backgroundColor: '#8bae74', fontWeight: 'bold' }}>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}>{t('projection')}</TableCell>
                        <TableCell align="right" sx={zoneStyle(zoneTotals.goals['TOTAL'] || zoneTotals.orders['TOTAL'] ? zoneTotals.projection['TOTAL'] : undefined)}>{zoneTotals.projection['TOTAL'].toFixed(2)}%</TableCell>
                        <TableCell align="right" sx={zoneStyle(zoneTotals.goals['FUSIOGAS'] || zoneTotals.orders['FUSIOGAS'] ? zoneTotals.projection['FUSIOGAS'] : undefined)}>{zoneTotals.projection['FUSIOGAS'].toFixed(2)}%</TableCell>
                        <TableCell align="right" sx={zoneStyle(zoneTotals.goals['HIDRO 3'] || zoneTotals.orders['HIDRO 3'] ? zoneTotals.projection['HIDRO 3'] : undefined)}>{zoneTotals.projection['HIDRO 3'].toFixed(2)}%</TableCell>
                        <TableCell align="right" sx={zoneStyle(zoneTotals.goals['HIDROFLEX'] || zoneTotals.orders['HIDROFLEX'] ? zoneTotals.projection['HIDROFLEX'] : undefined)}>{zoneTotals.projection['HIDROFLEX'].toFixed(2)}%</TableCell>
                        <TableCell align="right" sx={zoneStyle(zoneTotals.goals['RESTO'] || zoneTotals.orders['RESTO'] ? zoneTotals.projection['RESTO'] : undefined)}>{zoneTotals.projection['RESTO'].toFixed(2)}%</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}

                    {!isVendor(session) && (
                      <TableRow sx={{ backgroundColor: '#8bae74', fontWeight: 'bold' }}>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}>{t('clientsAnual')}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{((zoneTotals['clients']?.TOTAL.anual ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" sx={zoneStyle()} >{((zoneTotals['clients']?.FUSIOGAS.anual ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{((zoneTotals['clients']?.['HIDRO 3'].anual ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{((zoneTotals['clients']?.HIDROFLEX.anual ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{((zoneTotals['clients']?.RESTO.anual ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" sx={zoneStyle()} >{ }</TableCell>
                      </TableRow>
                    )}
                    {!isVendor(session) && (
                      <TableRow sx={{ backgroundColor: '#8bae74', fontWeight: 'bold' }}>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}>{t('clients')}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{((zoneTotals['clients']?.TOTAL.mes ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" sx={zoneStyle()} >{((zoneTotals['clients']?.FUSIOGAS.mes ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{((zoneTotals['clients']?.['HIDRO 3'].mes ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{((zoneTotals['clients']?.HIDROFLEX.mes ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" sx={zoneStyle()} >{((zoneTotals['clients']?.RESTO.mes ?? 0)).toFixed(0)}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>{ }</TableCell>
                      </TableRow>
                    )}

                    {!isVendor(session) && (
                      <TableRow sx={{ backgroundColor: '#8bae74', fontWeight: 'bold' }}>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}></TableCell>
                        <TableCell align="left" sx={zoneStyle()}>{t('scopeClients')}</TableCell>
                        <TableCell align="right" sx={zoneStyle()}>
                          {((zoneTotals['clients']?.TOTAL.anual
                            ? (zoneTotals['clients']?.TOTAL.mes / zoneTotals['clients']?.TOTAL.anual) * 100 : 0).toFixed(2) + '%')}
                        </TableCell>
                        <TableCell align="right" sx={zoneStyle()}>
                          {
                            ((zoneTotals['clients']?.FUSIOGAS.anual
                              ? (zoneTotals['clients']?.FUSIOGAS.mes / zoneTotals['clients']?.FUSIOGAS.anual) * 100 : 0).toFixed(2) + '%')
                          }
                        </TableCell>
                        <TableCell align="right" sx={zoneStyle()}>
                          {((zoneTotals['clients']?.['HIDRO 3'].anual
                            ? (zoneTotals['clients']?.['HIDRO 3'].mes / zoneTotals['clients']?.['HIDRO 3'].anual) * 100 : 0).toFixed(2) + '%')}
                        </TableCell>
                        <TableCell align="right" sx={zoneStyle()}>
                          {((zoneTotals['clients']?.HIDROFLEX.anual
                            ? (zoneTotals['clients']?.HIDROFLEX.mes / zoneTotals['clients']?.HIDROFLEX.anual) * 100 : 0).toFixed(2) + '%')}
                        </TableCell>
                        <TableCell align="right" sx={zoneStyle()}>
                          {((zoneTotals['clients']?.RESTO.anual
                            ? (zoneTotals['clients']?.RESTO.mes / zoneTotals['clients']?.RESTO.anual) * 100 : 0).toFixed(2) + '%')}
                        </TableCell>
                        <TableCell>{''}</TableCell>
                      </TableRow>
                    )}

                    {/* Vendedores de la zona */}

                    {
                      sortedGoals.filter(r => r.zone === zone).map((row) => {
                        const key = rowKey(row)
                        return (
                          <React.Fragment key={key}>
                            {['goals', 'orders', 'scope', 'goal2Unlock', 'deficit', 'projection', 'clientsAnual', 'clients', 'scopeClients'].map((type) => {
                              const typeLabel = (() => {
                                switch (type) {
                                  case 'goals': return t('budget');
                                  case 'orders': return t('orders');
                                  case 'scope': return t('scope');
                                  case 'goal2Unlock': return t('goal2Unlock');
                                  case 'deficit': return t('deficit');
                                  case 'projection': return t('projection');
                                  case 'clientsAnual': return t('clientsAnual');
                                  case 'clients': return t('clients');
                                  case 'scopeClients': return t('scopeClients');
                                }
                              })();

                              const baseBg = type === 'goals' ? darken(theme.palette.action.hover, 0.1) : 'inherit'
                              const rowKeyId = rowKey(row);
                              const uniqueKey = `${rowKeyId}::${type}`;
                              const isGoals = type === 'goals';

                              return (
                                <TableRow
                                  key={uniqueKey}
                                  ref={isGoals ? (el) => {
                                    const keyVal = rowKey(row);
                                    if (el) {
                                      rowRefs.current.set(keyVal, el);
                                    } else {
                                      rowRefs.current.delete(keyVal);
                                    }
                                  } : undefined}
                                  sx={{ backgroundColor: highlightedKeys.has(rowKeyId) ? 'rgba(25,118,210,0.2)' : baseBg }}
                                >
                                  {!isVendor(session) && <TableCell align="left">{type === 'goals' ? row.zone : ''}</TableCell>}
                                  <TableCell align="left">{type === 'goals' ? row.vendor : ''}</TableCell>
                                  <TableCell align="left"  sx={{ whiteSpace: 'nowrap' }}>{typeLabel}</TableCell>
                                  {type === 'goal2Unlock' ? (
                                    <>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          fontWeight: 'bold',
                                          color: 'white',
                                          backgroundColor:
                                            row.goal2UnlockPercentage === 100
                                              ? '#008457'
                                              : row.goal2UnlockPercentage && row.goal2UnlockPercentage > 0
                                                ? '#ed6c02'
                                                : '#d32f2f'
                                        }}
                                      >
                                        {row.goal2UnlockPercentage && row.goal2UnlockPercentage > 0
                                          ? `Habilitado ${row.goal2UnlockPercentage}%`
                                            : 'Bloqueado'}
                                      </TableCell>

                                      <TableCell />
                                      <TableCell />
                                      <TableCell />
                                      <TableCell />
                                      <TableCell />
                                    </>
                                  ) : type === 'scope' ? (                                    
                                      <>
                                        <TableCell align="right" sx={vendorStyle(row['goals']?.TOTAL || row['orders']?.TOTAL ? row[type]?.TOTAL : undefined)}>{((row[type]?.TOTAL ?? 0) * 100).toFixed(2)}%</TableCell>
                                        <TableCell align="right" sx={vendorStyle(row['goals']?.FUSIOGAS || row['orders']?.FUSIOGAS ? row[type]?.FUSIOGAS : undefined)}>{((row[type]?.FUSIOGAS ?? 0) * 100).toFixed(2)}%</TableCell>
                                        <TableCell align="right" sx={vendorStyle(row['goals']?.['HIDRO 3'] || row['orders']?.['HIDRO 3'] ? row[type]?.['HIDRO 3'] : undefined)}>{((row[type]?.['HIDRO 3'] ?? 0) * 100).toFixed(2)}%</TableCell>
                                        <TableCell align="right" sx={vendorStyle(row['goals']?.HIDROFLEX || row['orders']?.HIDROFLEX ? row[type]?.HIDROFLEX : undefined)}>{((row[type]?.HIDROFLEX ?? 0) * 100).toFixed(2)}%</TableCell>
                                        <TableCell align="right" sx={vendorStyle(row['goals']?.RESTO || row['orders']?.RESTO ? row[type]?.RESTO : undefined)}>{((row[type]?.RESTO ?? 0) * 100).toFixed(2)}%</TableCell>
                                        <TableCell align='right' sx={vendorStyle((row.anual ?? 0) / (row.annualGoals ?? 1))}>{(((row.anual ?? 0) / ((row.annualGoals ?? 1))) * 100).toFixed(2)}%</TableCell>
                                      </>
                                    ) : ((type === 'projection') ? (
                                      <>
                                        <TableCell align="right" sx={vendorStyle(row['goals']?.TOTAL || row['orders']?.TOTAL ? row[type]?.TOTAL : undefined)}>{((row[type]?.TOTAL ?? 0) * 100).toFixed(2)}%</TableCell>
                                        <TableCell align="right" sx={vendorStyle(row['goals']?.FUSIOGAS || row['orders']?.FUSIOGAS ? row[type]?.FUSIOGAS : undefined)}>{((row[type]?.FUSIOGAS ?? 0) * 100).toFixed(2)}%</TableCell>
                                        <TableCell align="right" sx={vendorStyle(row['goals']?.['HIDRO 3'] || row['orders']?.['HIDRO 3'] ? row[type]?.['HIDRO 3'] : undefined)}>{((row[type]?.['HIDRO 3'] ?? 0) * 100).toFixed(2)}%</TableCell>
                                        <TableCell align="right" sx={vendorStyle(row['goals']?.HIDROFLEX || row['orders']?.HIDROFLEX ? row[type]?.HIDROFLEX : undefined)}>{((row[type]?.HIDROFLEX ?? 0) * 100).toFixed(2)}%</TableCell>
                                        <TableCell align="right" sx={vendorStyle(row['goals']?.RESTO || row['orders']?.RESTO ? row[type]?.RESTO : undefined)}>{((row[type]?.RESTO ?? 0) * 100).toFixed(2)}%</TableCell>
                                        <TableCell align="right">{''}</TableCell>
                                      </>
                                    ) : ((type === 'clientsAnual') ? (
                                      <>
                                        <TableCell align="right" >{((row['clients']?.TOTAL.anual ?? 0)).toFixed(0)}</TableCell>
                                        <TableCell align="right" >{((row['clients']?.FUSIOGAS.anual ?? 0)).toFixed(0)}</TableCell>
                                        <TableCell align="right" >{((row['clients']?.['HIDRO 3'].anual ?? 0)).toFixed(0)}</TableCell>
                                        <TableCell align="right" >{((row['clients']?.HIDROFLEX.anual ?? 0)).toFixed(0)}</TableCell>
                                        <TableCell align="right" >{((row['clients']?.RESTO.anual ?? 0)).toFixed(0)}</TableCell>
                                        <TableCell align="right" >{ }</TableCell>
                                      </>
                                    ) : ((type === 'clients') ? (
                                      <>
                                        <TableCell align="right" >{((row[type]?.TOTAL.mes ?? 0)).toFixed(0)}</TableCell>
                                        <TableCell align="right" >{((row[type]?.FUSIOGAS.mes ?? 0)).toFixed(0)}</TableCell>
                                        <TableCell align="right" >{((row[type]?.['HIDRO 3'].mes ?? 0)).toFixed(0)}</TableCell>
                                        <TableCell align="right" >{((row[type]?.HIDROFLEX.mes ?? 0)).toFixed(0)}</TableCell>
                                        <TableCell align="right" >{((row[type]?.RESTO.mes ?? 0)).toFixed(0)}</TableCell>
                                        <TableCell align="right" >{ }</TableCell>
                                      </>
                                    ) : ((type === 'scopeClients') ? (
                                      <>
                                        <TableCell align="right">
                                          {((row['clients']?.TOTAL.anual
                                            ? (row['clients']?.TOTAL.mes / row['clients']?.TOTAL.anual) * 100 : 0).toFixed(2) + '%')}
                                        </TableCell>
                                        <TableCell align="right">
                                          {
                                            ((row['clients']?.FUSIOGAS.anual
                                              ? (row['clients']?.FUSIOGAS.mes / row['clients']?.FUSIOGAS.anual) * 100 : 0).toFixed(2) + '%')
                                          }
                                        </TableCell>
                                        <TableCell align="right">
                                          {((row['clients']?.['HIDRO 3'].anual
                                            ? (row['clients']?.['HIDRO 3'].mes / row['clients']?.['HIDRO 3'].anual) * 100 : 0).toFixed(2) + '%')}
                                        </TableCell>
                                        <TableCell align="right">
                                          {((row['clients']?.HIDROFLEX.anual
                                            ? (row['clients']?.HIDROFLEX.mes / row['clients']?.HIDROFLEX.anual) * 100 : 0).toFixed(2) + '%')}
                                        </TableCell>
                                        <TableCell align="right">
                                          {((row['clients']?.RESTO.anual
                                            ? (row['clients']?.RESTO.mes / row['clients']?.RESTO.anual) * 100 : 0).toFixed(2) + '%')}
                                        </TableCell>
                                        <TableCell>{''}</TableCell>
                                      </>
                                    ) : (<>
                                      <TableCell align="right">{formattedCurrency(row[type]?.TOTAL ?? 0)}</TableCell>
                                      <TableCell align="right">{formattedCurrency(row[type]?.FUSIOGAS ?? 0)}</TableCell>
                                      <TableCell align="right">{formattedCurrency(row[type]?.['HIDRO 3'] ?? 0)}</TableCell>
                                      <TableCell align="right">{formattedCurrency(row[type]?.HIDROFLEX ?? 0)}</TableCell>
                                      <TableCell align="right">{formattedCurrency(row[type]?.RESTO ?? 0)}</TableCell>
                                      <TableCell align="right">{type === 'goals' ? formattedCurrency(row.annualGoals ?? 0) :
                                        type === 'orders' ? formattedCurrency(row.anual ?? 0) : formattedCurrency(Math.max(0, (row.annualGoals ?? 0) - (row.anual ?? 0)))}</TableCell>
                                    </>
                                    )))))}
                                </TableRow>
                              )
                            })
                            }
                          </React.Fragment>
                        )
                      })
                    }
                  </React.Fragment>
                ))
              }
              )()}
            </TableBody>

          </Table>
          {isFetching &&
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
              <CircularProgress size={25} />
            </div>
          }
        </TableContainer>
      </Stack>
    </Stack >
  )
}

const mapStateToProps = (state: AppState) => ({
  session: state.session
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(GoalStatsVendorSSE)



